// 📄 pages/Proyectos/components/IncidenteFormModal.tsx
import React, { useState } from 'react';
import { X, Save, Paperclip, Trash2 } from 'lucide-react';
import {
  createIncidente,
  IncidenteFormData,
  IncidenteAmbiente,
  IncidentePrioridad,
  IncidenteFase,
  IncidentePlataforma,
  IncidenteCanal,
  IncidenteCategoriaSR,
  PRIORIDADES,
  AMBIENTES,
  FASES,
  FASE_LABELS,
  PLATAFORMAS,
  CANALES_INTEGRACION,
  CATEGORIAS_SR
} from '../../../services/incidenteService.ts';
import { ProjectMember } from '../../../services/projectService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { useAuth } from '../../../context/AuthContext.tsx';
import { COMMERCE_CHECKLISTS, buildChecklistStepKey } from '../../../data/commerceChecklistCatalog.ts';

interface IncidenteFormModalProps {
  projectId: string;
  solucionesActivas: string[];
  // Proyecto sin SAP Ariba: el campo es "Área" (opcional) en vez de "Solución relacionada".
  modoArea?: boolean;
  miembros: ProjectMember[];
  onClose: () => void;
  onSaved: () => void;
  // Modo SR (ver SRSection.tsx, pestaña "SR" de Integración > Commerce):
  // agrega Categoría/Sistema/Canal, fija fase="Proyecto" + solución
  // "commerce", y simplifica Ambiente a Test/PRD (Test guarda como QAS).
  srMode?: boolean;
  defaultCanal?: IncidenteCanal;
  // Precarga parcial del formulario — usado por "Crear incidente" desde un
  // caso de prueba Fallido (ver CasosPruebaSection.tsx): título/descripción/
  // solución/casoPruebaId ya vienen resueltos, el usuario solo completa lo
  // que falta (ambiente, prioridad, etc.) antes de guardar.
  prefill?: Partial<IncidenteFormData>;
  // Se dispara con el id del incidente recién creado, ANTES de onSaved —
  // el caller lo usa para guardar la referencia cruzada (linkIncidenteACaso).
  onCreated?: (incidenteId: string) => void;
}

export default function IncidenteFormModal({ projectId, solucionesActivas, modoArea = false, miembros, onClose, onSaved, srMode = false, defaultCanal, prefill, onCreated }: IncidenteFormModalProps) {
  const { user, profile } = useAuth();

  const [form, setForm] = useState<IncidenteFormData>({
    titulo: '',
    descripcion: '',
    reportadoPorNombre: user?.displayName || profile?.name || (user?.email ? user.email.split('@')[0] : ''),
    reportadoPorEmail: user?.email || profile?.email || '',
    fase: srMode ? 'Proyecto' : 'Soporte',
    solucionRelacionada: srMode ? 'commerce' : (modoArea ? '' : solucionesActivas[0] || ''),
    ambiente: srMode ? 'QAS' : 'DEV',
    prioridad: 'Media',
    asignadoA: '',
    sistema: undefined,
    canal: defaultCanal || '',
    categoriaSR: undefined,
    ...prefill
  });
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selección en cascada del paso de checklist (solo aplica cuando
  // fase === 'Proyecto' y solucionRelacionada === 'commerce').
  const [checklistId, setChecklistId] = useState(COMMERCE_CHECKLISTS[0].id);
  const [canalId, setCanalId] = useState(COMMERCE_CHECKLISTS[0].canales?.[0]?.id || '');
  const [stepId, setStepId] = useState(COMMERCE_CHECKLISTS[0].canales?.[0]?.steps?.[0]?.id || '');

  const handleChange = <K extends keyof IncidenteFormData>(field: K, value: IncidenteFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const esProyectoCommerce = form.fase === 'Proyecto' && form.solucionRelacionada === 'commerce';
  const checklistActivo = COMMERCE_CHECKLISTS.find(c => c.id === checklistId) || COMMERCE_CHECKLISTS[0];
  const canalActivo = checklistActivo.canales?.find(c => c.id === canalId);
  const pasosDisponibles = checklistActivo.canales ? (canalActivo?.steps || []) : (checklistActivo.steps || []);

  const handleChecklistChange = (nuevoChecklistId: string) => {
    setChecklistId(nuevoChecklistId);
    const nuevoChecklist = COMMERCE_CHECKLISTS.find(c => c.id === nuevoChecklistId);
    const nuevoCanalId = nuevoChecklist?.canales?.[0]?.id || '';
    setCanalId(nuevoCanalId);
    const nuevosPasos = nuevoChecklist?.canales
      ? nuevoChecklist.canales.find(c => c.id === nuevoCanalId)?.steps
      : nuevoChecklist?.steps;
    const nuevoStepId = nuevosPasos?.[0]?.id || '';
    setStepId(nuevoStepId);
    handleChange('checklistStepKey', buildChecklistStepKey(nuevoChecklistId, nuevoStepId, nuevoCanalId || undefined));
  };

  const handleCanalChange = (nuevoCanalId: string) => {
    setCanalId(nuevoCanalId);
    const nuevosPasos = checklistActivo.canales?.find(c => c.id === nuevoCanalId)?.steps || [];
    const nuevoStepId = nuevosPasos[0]?.id || '';
    setStepId(nuevoStepId);
    handleChange('checklistStepKey', buildChecklistStepKey(checklistId, nuevoStepId, nuevoCanalId));
  };

  const handleStepChange = (nuevoStepId: string) => {
    setStepId(nuevoStepId);
    handleChange('checklistStepKey', buildChecklistStepKey(checklistId, nuevoStepId, checklistActivo.canales ? canalId : undefined));
  };

  const handleFaseChange = (nuevaFase: IncidenteFase) => {
    handleChange('fase', nuevaFase);
    if (nuevaFase !== 'Proyecto') {
      handleChange('checklistStepKey', undefined);
    } else if (form.solucionRelacionada === 'commerce' && stepId) {
      handleChange('checklistStepKey', buildChecklistStepKey(checklistId, stepId, checklistActivo.canales ? canalId : undefined));
    }
  };

  const handleSolucionChange = (nuevaSolucion: string) => {
    handleChange('solucionRelacionada', nuevaSolucion);
    if (form.fase === 'Proyecto' && nuevaSolucion === 'commerce' && stepId) {
      handleChange('checklistStepKey', buildChecklistStepKey(checklistId, stepId, checklistActivo.canales ? canalId : undefined));
    } else {
      handleChange('checklistStepKey', undefined);
    }
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.titulo.trim()) { setError("El título es obligatorio."); return; }
    if (!form.descripcion.trim()) { setError("La descripción es obligatoria."); return; }
    if (!modoArea && solucionesActivas.length > 0 && !form.solucionRelacionada) { setError("Selecciona la solución relacionada."); return; }
    if (!srMode && esProyectoCommerce && !form.checklistStepKey) { setError("Selecciona el paso del checklist relacionado."); return; }
    if (srMode && !form.categoriaSR) { setError("Selecciona la categoría del SR."); return; }
    if (srMode && !form.sistema) { setError("Selecciona el sistema relacionado."); return; }

    setSaving(true);
    try {
      const incidenteId = await createIncidente(projectId, form, files, miembros);
      onCreated?.(incidenteId);
      onSaved();
    } catch (err: any) {
      setError(err.message || "Error al reportar el incidente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))]">
          <h3 className="text-lg font-medium text-white">{srMode ? 'Nuevo SR' : 'Reportar Incidente'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Título <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => handleChange('titulo', e.target.value)}
              placeholder="ej. Error 500 al guardar Orden de Compra"
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Descripción <span className="text-red-400">*</span>
            </label>
            <textarea
              value={form.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              rows={6}
              placeholder="Pasos para reproducir, mensaje de error, logs..."
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none font-mono"
            />
          </div>

          {srMode && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Categoría de SR <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.categoriaSR || ''}
                  onChange={(e) => handleChange('categoriaSR', e.target.value as IncidenteCategoriaSR)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Selecciona...</option>
                  {CATEGORIAS_SR.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Sistema <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.sistema || ''}
                  onChange={(e) => handleChange('sistema', e.target.value as IncidentePlataforma)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Selecciona...</option>
                  {PLATAFORMAS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Canal</label>
                <select
                  value={form.canal || ''}
                  onChange={(e) => handleChange('canal', e.target.value as IncidenteCanal)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Sin canal específico</option>
                  {CANALES_INTEGRACION.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Reportado por (nombre)</label>
              <input
                type="text"
                value={form.reportadoPorNombre}
                onChange={(e) => handleChange('reportadoPorNombre', e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Reportado por (email)</label>
              <input
                type="email"
                value={form.reportadoPorEmail}
                onChange={(e) => handleChange('reportadoPorEmail', e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {!srMode && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Fase</label>
              <select
                value={form.fase}
                onChange={(e) => handleFaseChange(e.target.value as IncidenteFase)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              >
                {FASES.map(f => <option key={f} value={f}>{FASE_LABELS[f]}</option>)}
              </select>
            </div>
          )}

          <div className={`grid grid-cols-1 ${srMode ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`}>
            {!srMode && solucionesActivas.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  {modoArea ? 'Área' : <>Solución relacionada <span className="text-red-400">*</span></>}
                </label>
                <select
                  value={form.solucionRelacionada}
                  onChange={(e) => handleSolucionChange(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  {modoArea && <option value="">Sin área</option>}
                  {solucionesActivas.map(slug => (
                    <option key={slug} value={slug}>{SOLUCIONES[slug]?.label || slug}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Ambiente</label>
              {srMode ? (
                <div className="flex bg-zinc-800 border border-white/10 rounded-lg p-1 gap-1">
                  {(['QAS', 'PRD'] as const).map((amb) => (
                    <button
                      key={amb}
                      type="button"
                      onClick={() => handleChange('ambiente', amb)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                        form.ambiente === amb
                          ? amb === 'PRD' ? 'bg-red-500 text-[#fff]' : 'bg-blue-500 text-[#fff]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {amb === 'QAS' ? 'Test' : 'PRD'}
                    </button>
                  ))}
                </div>
              ) : (
                <select
                  value={form.ambiente}
                  onChange={(e) => handleChange('ambiente', e.target.value as IncidenteAmbiente)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  {AMBIENTES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Prioridad</label>
              <select
                value={form.prioridad}
                onChange={(e) => handleChange('prioridad', e.target.value as IncidentePrioridad)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              >
                {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {!srMode && form.fase === 'Proyecto' && (
            esProyectoCommerce ? (
              <div className={`grid grid-cols-1 ${checklistActivo.canales ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Checklist</label>
                  <select
                    value={checklistId}
                    onChange={(e) => handleChecklistChange(e.target.value)}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    {COMMERCE_CHECKLISTS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                {checklistActivo.canales && (
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Canal</label>
                    <select
                      value={canalId}
                      onChange={(e) => handleCanalChange(e.target.value)}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                    >
                      {checklistActivo.canales.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    Paso <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={stepId}
                    onChange={(e) => handleStepChange(e.target.value)}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    {pasosDisponibles.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Paso del checklist</label>
                <select disabled className="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-2.5 text-sm text-gray-500 cursor-not-allowed">
                  <option>Checklist no implementado para esta solución</option>
                </select>
              </div>
            )
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Asignar a (opcional)</label>
            <select
              value={form.asignadoA}
              onChange={(e) => handleChange('asignadoA', e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Sin asignar</option>
              {miembros.map(m => (
                <option key={m.userId} value={m.userId}>{m.email} ({m.rol})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Adjuntos (capturas, logs)</label>
            <label className="flex items-center gap-2 justify-center border border-dashed border-white/20 rounded-lg p-3 text-xs text-gray-400 hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all">
              <Paperclip className="w-4 h-4" />
              Seleccionar archivos
              <input type="file" multiple onChange={handleAddFiles} className="hidden" />
            </label>

            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => handleRemoveFile(idx)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : (srMode ? 'Crear SR' : 'Reportar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
